import { TextField } from '@mui/material'
import { DatePicker, DateTimePicker, LocalizationProvider, PickersActionBarAction } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import React, { useRef } from 'react'

import {
  getJurisdictionTimezone,
  removeSeconds,
  stringToZonedDateTime,
  temporalToDayJS,
  zonedDateTimeToISOString,
} from 'src/commons/temporalHelpers'

interface IProps {
  clearable?: boolean
  disableFuture?: boolean
  disabled?: boolean
  error?: boolean
  inputProps?: { [key: string]: string }
  label?: string
  maxDate?: Temporal.ZonedDateTime
  maxDateMessage?: string
  minDate?: Temporal.ZonedDateTime
  onChange: (date?: Temporal.ZonedDateTime) => void
  onValidityChange?: (isValid: boolean) => void
  required?: boolean
  sx?: { [key: string]: number }
  value?: Temporal.ZonedDateTime
  withTime?: boolean
}

const SimpleDatePicker = (props: IProps) => {
  const {
    value,
    onChange,
    disableFuture,
    clearable,
    label,
    minDate,
    maxDate,
    error,
    withTime,
    disabled,
    maxDateMessage,
    sx,
    inputProps,
    required,
    onValidityChange,
  } = props

  const pickerRef = useRef(null)

  const onDateChange = (date: dayjs.Dayjs | null) => {
    const isValid = !date || date.isValid()
    onValidityChange?.(isValid)
    if (!isValid) {
      return
    }
    let dateTime = undefined
    if (date !== null) {
      const t = stringToZonedDateTime(date.format())
      dateTime = withTime ? removeSeconds(t) : t.startOfDay()
    }
    onChange(dateTime)
  }

  const pickerProps = {
    value: temporalToDayJS(value),
    onChange: onDateChange,
    maxDateMessage: maxDateMessage,
    disableFuture,
    clearable,
    label,
    minDate: minDate ? dayjs(zonedDateTimeToISOString(minDate)) : undefined,
    maxDate: maxDate ? dayjs(zonedDateTimeToISOString(maxDate)) : undefined,
    error,
    disabled,
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    e.preventDefault()
  }

  const actions: PickersActionBarAction[] = []
  if (props.clearable) {
    actions.push('clear')
  }
  actions.push('cancel')
  actions.push('accept')

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjs.locale()}>
      <div ref={pickerRef} />
      {withTime ? (
        <DateTimePicker
          {...pickerProps}
          format={'L LT'}
          timezone={getJurisdictionTimezone()}
          slots={{
            textField: (props) => <TextField onKeyDown={onKeyDown} {...props} inputProps={inputProps} />,
          }}
          slotProps={{
            actionBar: {
              actions: actions,
            },
            popper: { anchorEl: pickerRef.current },
            textField: { required },
          }}
          sx={sx}
        />
      ) : (
        <DatePicker {...pickerProps} format={'L'} slotProps={{ textField: { required } }} sx={sx} />
      )}
    </LocalizationProvider>
  )
}

export default SimpleDatePicker
